import { useContext, useMemo, useEffect, useState, useCallback, useRef } from 'react'
import { CompareArrows, Delete, Edit, ImportExport } from '@mui/icons-material'
import {
  Typography,
  Badge,
  Menu,
  MenuItem,
  CircularProgress,
  Button,
  Fab,
  TableCell,
  TableRow,
  Checkbox,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Component as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useRouteMatch, useHistory, useLocation, Prompt } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'
import uuid from 'uuid/v4'
import { CvsAutoComplete, CvsDrawer, CvsButton, Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { fieldSetContext, modalContext, usePageTitleContext } from '@contexts'
import { colors } from '@global/constants'
import { api } from 'api'
import { getCellContent, showSnackbar } from 'functions'
import { TargetSelector } from './target-selector'
import { CvsTable } from '../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../components/table/enhanced-table-toolbar'
import { EditField } from '../../field-governance/edit-field'
import { Fuzzy } from '../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
  badge: {
    backgroundColor: 'rgba(0,0,0,.15)',
    height: '16px',
    width: '16px',
    minWidth: '16px',
  },
}))

export const Fields = () => {
  const classes = useStyles()
  const {
    fetchFieldSet,
    saveFieldSet,
    byId,
    fetchDuplicateFieldSet,
    setNewFieldSet,
    removeNewFieldSet,
  } = useContext(fieldSetContext)
  const { overridePageTitle } = usePageTitleContext()

  const match = useRouteMatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const openFieldId = searchParams.get('open')

  const { fieldSetId } = match.params
  const [shouldDisplayExitIntentPrompt, setShouldDisplayExitIntentPrompt] = useState(false)
  const fieldSet = useMemo(() => byId[fieldSetId] || {}, [byId, fieldSetId])
  const [selectedField, setSelectedField] = useState(openFieldId ?? null)
  const ModalContext = useContext(modalContext)
  const [tempFieldset, setTempFieldset] = useState(fieldSet || { fields: [] })
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const dispatch = useDispatch()
  const history = useHistory()
  const { fields = null } = fieldSet
  const fieldsById = useMemo(
    () =>
      fieldSet.fields &&
      fieldSet.fields.reduce((acc, field) => ({ ...acc, [field.id]: field }), {}),
    [fieldSet],
  )

  const buttonRef = useRef(null)
  const initialFieldSetRef = useRef(tempFieldset)
  const [rawTargets, setRawTargets] = useState({})
  const [targets, setTargets] = useState([])
  const [selectedTarget, setSelectedTarget] = useState(null)

  const [rawLists, setRawLists] = useState({})
  const [lists, setLists] = useState([])
  const [selectedList, setSelecedList] = useState(null)
  const [filterByType, setFilterByType] = useState(null)
  const [filterByListType, setFilterByListType] = useState(null)

  const [menuOpen, setMenuOpen] = useState(false)

  const [importModalOpen, setImportModalOpen] = useState(false)

  const field = useMemo(
    () => selectedField && fieldsById && fieldsById[selectedField],
    [selectedField, fieldsById],
  )

  useEffect(() => {
    if (
      initialFieldSetRef.current.description !== tempFieldset.description ||
      initialFieldSetRef.current.name !== tempFieldset.name
    ) {
      setShouldDisplayExitIntentPrompt(true)
    }
  }, [tempFieldset.description, tempFieldset.name])

  useDeepCompareEffect(() => {
    if (!tempFieldset?.name) return
    const templateFieldsetName = tempFieldset.name
    overridePageTitle(t`Edit ${templateFieldsetName} - Claravine`)

    return () => {
      overridePageTitle(null)
    }
  }, [tempFieldset])

  useEffect(() => {
    api.get('/api/v1/account/targets/targetsForImport').then(({ data }) => {
      setTargets(data.map(t => ({ label: t.name, value: t.id })))
      setRawTargets(
        data.reduce(
          (memo, item) => ({
            ...memo,
            [item.id]: item,
          }),
          {},
        ),
      )
    })

    api.get('/api/v1/account/lists/').then(({ data }) => {
      setLists(data.map(t => ({ label: t.name, value: t.id })))
      setRawLists(
        data.reduce(
          (memo, item) => ({
            ...memo,
            [item.id]: item,
          }),
          {},
        ),
      )
    })

    return function remove() {
      removeNewFieldSet()
    }
  }, [])

  useEffect(() => {
    if (fieldSet) {
      setTempFieldset(fieldSet)
      if (initialFieldSetRef.current) {
        initialFieldSetRef.current = fieldSet
      }
    }
  }, [fieldSet])

  const didSave = response => {
    setSearchTerm('')
    setSaving(false)
    setSelected({})
    if (fieldSetId === 'new') {
      history.replace(`/admin/fieldsets/${response.id}/fields`)
    }

    dispatch(
      showSnackbar({
        message: t`Field Set saved successfully`,
        type: 'SUCCESS',
        duration: 5000,
      }),
    )
  }

  const failedToSave = (error = {}) => {
    if (error?.message || error?.response?.data?.message) {
      dispatch(
        showSnackbar({
          message: error?.message || error?.response?.data?.message,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    } else {
      dispatch(
        showSnackbar({
          message: t`Something went wrong trying to save this Field Set.`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }

  const handleSubmit = useCallback(async () => {
    setSelectedField(null)
    setSaving(true)

    const newFieldSet = {
      ...fieldSet,
      fields: tempFieldset.fields.map(f => {
        if (typeof f.id === 'string' && f.id.includes('TEMP_')) {
          delete f.id
        }
        return f
      }),
      name: tempFieldset.name,
      description: tempFieldset.description,
    }
    setShouldDisplayExitIntentPrompt(false)

    try {
      const saveFieldsetResponse = await saveFieldSet(newFieldSet)
      didSave(saveFieldsetResponse)
    } catch (error) {
      failedToSave(error)
    }
  }, [fieldSet, saveFieldSet, tempFieldset, failedToSave, didSave])

  const [targetSelector, setTargetSelector] = useState(false)

  const [selected, setSelected] = useState({})
  const [deleteIsDisabled, setDeleteIsDisabled] = useState(false)

  const columns = [
    { label: t`Name`, width: '30%', lookup: 'name' },
    { label: t`Type`, width: '30%', lookup: 'type' },
    { label: t`Updated Date`, type: 'date', width: '20%', lookup: 'updated_at' },
    { label: t`Required`, type: 'date', width: '15%', lookup: 'required' },
    { label: t`Target Mapping`, width: '15%', lookup: 'description' },
  ]

  const [displayFields, setDisplayFields] = useState(fieldSet.fields || [])

  useEffect(() => {
    setDeleteIsDisabled(false)
    const fieldWithPoweredPicklistSelected = (() => {
      const results = fields?.filter(result =>
        [selected].some(el => {
          const key = Object.keys(el)[0]
          return (
            // check if the powered_template_id field has a value
            // the key exists in the 'selected' state
            !!el[key] && // value for key is 'true'
            +key === +result.id &&
            result.list_governance?.lists?.powered_template_id
          )
        }),
      )
      return results?.length > 0
    })()

    if (fieldWithPoweredPicklistSelected) {
      setDeleteIsDisabled(true)
    }
  }, [selected])

  useEffect(() => {
    filterChanged(searchTerm)
  }, [tempFieldset, searchTerm])

  useEffect(() => {
    if (fieldSetId !== 'new') {
      fetchFieldSet(fieldSetId)
    } else {
      location.search.includes('duplicate')
        ? fetchDuplicateFieldSet(location.search.split('=')[1])
        : setNewFieldSet()
    }
  }, [fetchFieldSet, fieldSetId])

  const filterChanged = search => {
    setSearchTerm(search)
    setDisplayFields(!search ? tempFieldset.fields : fuzzySearch(search))
  }

  const fuzzySearch = search => {
    const fuzzy = new Fuzzy(tempFieldset.fields, {
      keys: ['name'],
    })
    return fuzzy.search(search)
  }

  const onReset = () => {}

  const editItems = () => {
    const item = parseInt(
      Object.keys(selected).find(key => selected[key]),
      10,
    )
    setSelectedField(item)
  }

  const addItem = () => {
    setSelectedField({})
  }

  const deleteItems = payload => {
    const newFields = [...tempFieldset.fields].filter(f => !payload[f.id])
    setSelected({})
    const newFieldSet = {
      ...fieldSet,
      fields: newFields.map(f => {
        if (typeof f.id === 'string' && f.id.includes('TEMP_')) {
          delete f.id
        }
        return f
      }),
      name: tempFieldset.name,
      description: tempFieldset.description,
    }
    saveFieldSet(newFieldSet).then(didSave).catch(failedToSave)
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Fields?`,
      description: t`Fields are integral to powering templates. Deleting fields that are currently used by a template could prevent users from making submissions`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const importFields = () => {
    const target = rawTargets[selectedTarget]
    setImportModalOpen(false)
    const importedFields = target.target_fields

    const newFields = importedFields.map(imf => {
      return {
        name: imf.name,
        id: `TEMP_${uuid()}`,
        governance_type: 'text',
        validation: {},
        transformation: {},
        target_fields: [imf],
      }
    })

    // const newFields = [...fieldSet.fields].map(f => (f.id === form.id ? form : f))
    const newFieldSet = {
      ...tempFieldset,
      fields: [...tempFieldset.fields, ...newFields],
      name: tempFieldset.name,
      description: tempFieldset.description,
    }
    setTempFieldset(newFieldSet)
  }

  const importListFields = () => {
    const list = rawLists[selectedList]
    setImportModalOpen(false)
    const importedFields = list.list_columns

    const newFields = importedFields.map(imf => {
      return {
        name: imf.name,
        id: `TEMP_${uuid()}`,
        governance_type: 'list',
        list_governance: {
          lists: rawLists[selectedList],
          list_id: selectedList,
          list_column_id: imf.id,
        },
      }
    })

    // const newFields = [...fieldSet.fields].map(f => (f.id === form.id ? form : f))
    const newFieldSet = {
      ...tempFieldset,
      fields: [...tempFieldset.fields, ...newFields],
      name: tempFieldset.name,
      description: tempFieldset.description,
    }
    setTempFieldset(newFieldSet)
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  const getListText = row => {
    // TODO: if we want column name we gotta go grab the list context and look up the column name.
    if (row.list_governance?.lists) {
      const listName = row.list_governance.lists?.name
      return t`List (${listName})`
    }
    return t`List (list has been deleted)`
  }

  const updateField = form => {
    setShouldDisplayExitIntentPrompt(true)
    setSelectedField('')
    if (!form.id) {
      form.id = `TEMP_${uuid()}`
    }
    const index = tempFieldset.fields.findIndex(f => f.id == form.id)
    if (index > -1) {
      const newFields = tempFieldset.fields
      newFields.splice(index, 1, form)
      setTempFieldset({ ...tempFieldset, fields: newFields })
      setDisplayFields(currentFields => [...currentFields, ...newFields])
    } else {
      setTempFieldset({ ...tempFieldset, fields: [...tempFieldset.fields, form] })
      setDisplayFields(() => [...tempFieldset.fields, form])
    }
    setShouldDisplayExitIntentPrompt(true)
  }

  const fieldsWithListTypesSet = new Set(
    tempFieldset?.fields
      ?.filter(f => f.governance_type === 'list')
      ?.map(f => f?.list_governance?.lists?.type),
  )

  const deleteTooltipTitle = deleteIsDisabled
    ? t`A selected field is being used in a template that powers a list. To delete, please first unpower the associated list.`
    : t`Delete`

  const selectedTemplateField = tempFieldset?.fields?.find(f => f.id == selectedField)

  const labels = {
    list: t`List`,
    autonumber: t`Autonumber`,
    autofill: t`Autofill`,
    concatenation: t`Concatenation`,
    constant: t`Constant`,
    date: t`Date`,
    text: t`Text`,
    creative: t`Content Comprehension`,
  }

  const listTypes = {
    dependent: t`Dependent`,
    'drill-down': t`Drill Down`,
    'drop-down': t`Drop Down`,
  }

  return !fields ? (
    <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
      <CircularProgress color="primary" />
    </div>
  ) : (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Field sets`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`A collection of columns that can be used within a submission dataset`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper className={classes.paper} data-test-page-id="edit-fieldset">
        <div style={{ padding: '24px 24px 10px', display: 'inline-flex' }}>
          <div>
            <TextField
              name="fieldset-name"
              required
              label={t`Field set name`}
              variant="outlined"
              value={tempFieldset.name || ''}
              onChange={e => {
                const { value } = e.target
                setTempFieldset(old => ({ ...old, name: value }))
              }}
              style={{ minWidth: 230, marginRight: 12 }}
            />
            <TextField
              name="fieldset-description"
              label={t`Description`}
              variant="outlined"
              value={tempFieldset.description || ''}
              onChange={e => {
                const { value } = e.target
                setTempFieldset(old => ({ ...old, description: value }))
              }}
              style={{ minWidth: 230 }}
            />
          </div>
        </div>
        <div className={classes.table} style={{ height: 'calc(100% - 85px)' }}>
          <EnhancedTableToolbar
            numSelected={numSelected}
            filterChanged={filterChanged}
            addItem={addItem}
            term={searchTerm}
            editItems={editItems}
            deleteItems={deleteItems}
            noAdd={false}
            onReset={onReset}
            blockMultipleEdit={false}
            specialAdd={
              <>
                <Fab
                  color="secondary"
                  size="small"
                  ref={buttonRef}
                  aria-label="Add"
                  disabled={!tempFieldset.name}
                  onClick={() => {
                    setMenuOpen(true)
                  }}
                >
                  <ImportExport />
                </Fab>
                <Menu
                  id="simple-menu"
                  anchorEl={buttonRef.current}
                  open={menuOpen}
                  onClose={() => {
                    setMenuOpen(false)
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setImportModalOpen('TARGETS')
                      setMenuOpen(false)
                    }}
                  >
                    {t`Import from targets`}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setImportModalOpen('LISTS')
                      setMenuOpen(false)
                    }}
                  >
                    {t`Import from lists`}
                  </MenuItem>
                </Menu>
              </>
            }
            filteringFields={
              <>
                <CvsAutoComplete
                  label={t`Filter by type`}
                  name="filter-by-type"
                  isClearable={true}
                  options={Object.keys(labels).map(v => ({ label: v, value: v }))}
                  onSelected={item => {
                    const filteredFields = tempFieldset?.fields.filter(
                      field => field.governance_type === item,
                    )
                    setDisplayFields(item ? filteredFields : tempFieldset?.fields)
                    setFilterByType(item)
                  }}
                  selected={filterByType}
                  margin="dense"
                  styleOverrides={{
                    container: base => ({
                      ...base,
                      minWidth: 165,
                      '& .MuiTextField-root': {
                        marginTop: 0,
                        marginBottom: 0,
                      },
                      '& .MuiTypography-body1': {
                        fontSize: 13,
                      },
                    }),
                  }}
                />
                {filterByType === 'list' && fieldsWithListTypesSet.size > 0 && (
                  <CvsAutoComplete
                    label={t`List type`}
                    name="filter-by-list-type"
                    isClearable={true}
                    options={Object.keys(listTypes).map(v => ({ label: listTypes[v], value: v }))}
                    onSelected={item => {
                      const filteredFieldsByListType = tempFieldset?.fields.filter(
                        field => field?.list_governance?.lists?.type === item,
                      )

                      if (item) {
                        setDisplayFields(filteredFieldsByListType)
                      } else {
                        const filteredFields = tempFieldset?.fields.filter(
                          field => field.governance_type === filterByType,
                        )
                        setDisplayFields(filteredFields)
                      }

                      setFilterByListType(item)
                    }}
                    selected={filterByListType}
                    margin="dense"
                    styleOverrides={{
                      container: base => ({
                        ...base,
                        minWidth: 165,
                        '& .MuiTextField-root': {
                          marginTop: 0,
                          marginBottom: 0,
                        },
                        '& .MuiTypography-body1': {
                          fontSize: 13,
                        },
                      }),
                    }}
                  />
                )}
              </>
            }
          >
            <Tooltip title={t`Edit`}>
              <Fab
                color="primary"
                style={{ color: 'white', marginRight: '10px' }}
                size="small"
                aria-label="Add"
                className={classes.fab}
                disabled={numSelected > 1}
                onClick={editItems}
              >
                <Edit />
              </Fab>
            </Tooltip>
            <Tooltip title={deleteTooltipTitle}>
              <span>
                <Fab
                  style={{
                    backgroundColor: deleteIsDisabled ? 'rgba(0, 0, 0, 0.12)' : colors.red.light,
                    color: 'white',
                  }}
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                  disabled={deleteIsDisabled}
                >
                  <Delete />
                </Fab>
              </span>
            </Tooltip>
          </EnhancedTableToolbar>
          <div style={{ height: 'calc(100% - 64px)' }}>
            <CvsTable
              columns={columns}
              rows={displayFields}
              orderBy="name"
              sortOrder="asc"
              keyField="id"
              noAdd={false}
              addItem={addItem}
              noPagination={true}
              style={{ maxWidth: '100%' }}
              multiSelect={true}
              selectAll={val => {
                setSelected(
                  displayFields.reduce(
                    (memo, item) => ({
                      ...memo,
                      [item.id]: val,
                    }),
                    {},
                  ),
                )
              }}
            >
              {({ row }) => (
                <TableRow
                  hover
                  data-test-fieldid={row.id}
                  onClick={() => {
                    setSelected({
                      ...selected,
                      [row.id]: !selected[row.id],
                    })
                    setSelectedField(row.id)
                  }}
                  tabIndex={-1}
                  key={row.id}
                  style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={e => {
                        e.stopPropagation()
                        setSelected({
                          ...selected,
                          [row.id]: !selected[row.id],
                        })
                      }}
                      checked={selected[row.id]}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <div>{row.name}</div>
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.governance_type === 'list' ? (
                      <>
                        <div>{getListText(row)}</div>
                        {row.list_governance?.delimiter && (
                          <div style={{ fontSize: 11 }}>
                            <strong>{t`Delimiter:`}</strong> {row.list_governance?.delimiter}
                          </div>
                        )}
                        <div style={{ fontSize: 11 }}>
                          <strong>{t`Type:`}</strong> {row.list_governance?.lists?.type}
                        </div>
                      </>
                    ) : (
                      labels[row.governance_type]
                    )}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {getCellContent('date', row.updated_at)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Checkbox
                      disabled
                      checked={Boolean(row.required)}
                      value={Boolean(row.required)}
                    />
                  </TableCell>
                  <TableCell
                    onClick={() => setTargetSelector(true)}
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Badge
                      badgeContent={row.target_fields?.length}
                      classes={{ badge: classes.badge }}
                    >
                      <CompareArrows data-test-cmp-id="mapping-icon" />
                    </Badge>
                  </TableCell>
                </TableRow>
              )}
            </CvsTable>
          </div>
        </div>
        <CvsDrawer
          data-test-cmp-id="drawer-edit-field"
          title={field ? `${t`Edit`} ${field.name}` : t`Edit new field`}
          visible={
            Boolean(selectedField && !targetSelector) && (!openFieldId || selectedTemplateField)
          }
          onClose={() => {
            setSelectedField('')
            setSelected({})
            if (openFieldId) {
              const newSearchParams = new URLSearchParams(location.search)
              newSearchParams.delete('open')
              history.replace({ search: newSearchParams.toString() })
            }
          }}
        >
          <EditField
            close={() => {
              setSelectedField('')
              setSelected({})
            }}
            submit={updateField}
            fieldSetId={fieldSetId}
            fieldId={selectedField ? String(selectedField) : ''}
            field={selectedTemplateField}
          />
        </CvsDrawer>
        <CvsDrawer
          onClose={() => {
            setSelectedField('')
            setTargetSelector(false)
            setSelected({})
          }}
          visible={Boolean(selectedField && targetSelector)}
          title={field ? `${t`Select target`} - ${field.name}` : t`Select target`}
        >
          <TargetSelector
            close={() => {
              setSelectedField('')
              setSelected({})
              setShouldDisplayExitIntentPrompt(false)
            }}
            field={tempFieldset.fields && tempFieldset.fields.find(f => f.id == selectedField)}
            fieldSet={tempFieldset}
            onDone={updateField}
          />
        </CvsDrawer>
      </Paper>
      <div style={{ padding: 12, paddingRight: 100, textAlign: 'right' }}>
        <CvsButton
          name="save-fieldset"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!tempFieldset.name || saving}
        >
          {saving ? t`Saving` : t`Save`}
        </CvsButton>
      </div>
      <Dialog
        onClose={() => {
          setImportModalOpen(false)
        }}
        open={!!importModalOpen}
      >
        {importModalOpen === 'TARGETS' ? (
          <>
            <DialogTitle>{t`Import fields from target`}</DialogTitle>
            <div style={{ padding: 24, paddingTop: 8 }}>
              <Typography>
                {t`Select a target to import fields. After the fields are loaded you will be able to govern them as you would any other field.`}
              </Typography>
              <CvsAutoComplete
                label={t`Select target`}
                options={targets}
                selected={selectedTarget}
                onSelected={item => {
                  setSelectedTarget(item)
                }}
              />
            </div>
            <DialogActions style={{ display: 'flex' }}>
              <Button
                onClick={() => {
                  setImportModalOpen(false)
                }}
                color="primary"
              >
                {t`Cancel`}
              </Button>
              <Button
                onClick={() => {
                  importFields()
                }}
                variant="outlined"
                color="primary"
                autoFocus
                disabled={!selectedTarget}
              >
                {t`Confirm`}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{t`Import fields from lists`}</DialogTitle>
            <div style={{ padding: 24, paddingTop: 8 }}>
              <Typography>
                {t`Select a list to import fields from. This will create a new governend field for each column in the selected list.`}
              </Typography>
              <CvsAutoComplete
                label={t`Select list`}
                options={lists}
                selected={selectedList}
                onSelected={item => {
                  setSelecedList(item)
                }}
              />
            </div>
            <DialogActions style={{ display: 'flex' }}>
              <Button
                onClick={() => {
                  setImportModalOpen(false)
                }}
                color="primary"
              >
                {t`Cancel`}
              </Button>
              <Button
                onClick={() => {
                  importListFields()
                }}
                variant="outlined"
                color="primary"
                autoFocus
                disabled={!setSelecedList}
              >
                {t`Confirm`}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Prompt
        when={shouldDisplayExitIntentPrompt}
        message={() =>
          t`You are about to lose any unsaved changes by leaving this page. Are you sure you want to continue?`
        }
      />
    </motion.div>
  )
}
